<template>
  <div class="receiving-locations-map">
    <place-search @input="v => focusOnMap(v, $refs.rlmap)" />
    <GmapMap
      ref="rlmap"
      class="rl-map"
      :center="mapCenter"
      :zoom="7"
      :options="{
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        disableDefaultUi: false
      }"
    >
      <gmap-info-window
        :options="infoOptions"
        :position="infoWindowPos"
        :opened="infoWinOpen"
        @closeclick="infoWinOpen=false">
      </gmap-info-window>

      <gmap-marker v-for="(m, index) in filteredMarkers"
        :position="m.geo"
        :clickable="true"
        :icon="returnPinSvg(m.category.entity.color.color)"
        @click="centerAndShowInfo(m, index)"
        :key="index"
      >
      </gmap-marker>

    </GmapMap>

    <category-filter
      :categories="categories.list"
      @input="applyCatFilter"
    />

  </div>
</template>

<script>
import MapUtils from '../mixins/map-utils'
import PlaceSearch from './PlaceSearch'
import CategoryFilter from './ProductCategoryFilter'

export default {
  components: { PlaceSearch, CategoryFilter },
  mixins: [MapUtils],
  data() {
    return {
      infoQuery: `query {
          langs: availableLanguages{
            id,
            argument,
            isDefault
          }
          langCurrent: languageInterfaceContext {
            id,
            argument
          }
          location: nodeQuery(
            filter: {
              conditions:[
                {
                  operator: EQUAL,
                  field: "type",
                  value: "receiving_locations",
                  enabled: true
                }
              ]
            }
          ) {
            count
          }
          category: taxonomyTermQuery(
            filter: {
              conditions: [
                { field: "vid", operator: EQUAL, value: ["receiving_location_categories"], enabled: true }
              ]
            }
          ) {
            count,
          }
        }
      `,
      contentQuery: `
        query GET_LOCATIONS($limit:Int!, $catLimit:Int!, $lang:LanguageId!) {
          categories: taxonomyTermQuery(
            limit: $catLimit,
            filter: {
              conditions: [
                { field: "vid", operator: EQUAL, value: ["receiving_location_categories"], enabled: true }
              ]
            }
          ) {
            list: entities {
              name: entityLabel,
              tid: entityId,
              ... on TaxonomyTermReceivingLocationCategories {
                color: fieldC {
                  color
                }
              }
            }
          }
          locations: nodeQuery(
            limit: $limit,
              filter: {
                conditions:[
                  {
                    operator: EQUAL,
                    field: "type",
                    value: "receiving_locations",
                    enabled: true,
                    language: $lang
                  }
                ]
              }
            ) {
            list: entities {
              ...on NodeReceivingLocations {
                nid,
                title,
                geo: fieldRecLocMap{
                  lat,
                  lon
                },
                email: fieldRecLocEmail,
                phone: fieldRecLocPhone,
                address1: fieldRecLocAddress,
                address2: fieldRecLocAddress2,
                region:fieldRecLocRegion {
                  entity {
                    title: entityLabel,
                    tid: entityId
                  }
                }
                categories: fieldCategory{
                  tid: targetId,
                  entity {
                    ...on TaxonomyTermReceivingLocationCategories {
                      title: entityLabel,
                      color: fieldC { color }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      locations: { list: [] },
      categories: { list: [] },
      catFilter: [],
      mapCenter: {lat:0, lng:0},
      languages: [],
      languageCurrent: {},
      currentMidx: null,
      infoWindowPos: null,
      infoWinOpen: false,
      infoOptions: {
        content: '',
        pixelOffset: {
          width: 0,
          height: -35
        }
      }
    }
  },
  async mounted () {
    try {
      const info = await this.$axios.post(`${this.getUrl}/graphql`, {
        query: this.infoQuery
      })
      let { data } = info.data
      this.locations.count = data.location.count
      this.categories.count = data.category.count
      this.languages = [...data.langs]
      this.languageCurrent = { ...data.langCurrent }
      const res = await this.$axios.post(`${this.getUrl}/graphql`, {
        query: this.contentQuery,
        variables: {
          limit: this.locations.count,
          catLimit: this.categories.count,
          lang: this.langToEnum(this.languageCurrent)
        }
      })
      this.locations.list = [...res.data.data.locations.list]
      let cats = res.data.data.categories.list
      this.categories.list = [...cats]
      this.$refs.rlmap.$mapPromise.then(map => { this.processBounds(map, this.locations.list) });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
    }
  },
  methods: {
    applyCatFilter(v) {
      this.catFilter = v
      this.$refs.rlmap.$mapPromise.then(map => { this.processBounds(map, this.locations.list) });
    },
    centerAndShowInfo(marker, index) {
      this.mapCenter = marker.geo
      this.infoWindowPos = marker.geo
      if (this.currentMidx !== index) {
        setTimeout(() => {
          this.$refs.rlmap.panBy(0,-120)
        }, 100)
      }
      this.infoOptions.content = `<h2 class="location-title">${this.returnValue(marker.title)}</h2>
        <div class="location-content">
        <p class="address-line">
          <strong>Address:</strong>
          <a target="_blank" href="https://maps.google.com/?q=${this.returnValue(marker.address1)} ${this.returnValue(marker.address2)}">${this.returnValue(marker.address1)}<br/>${this.returnValue(marker.address2)}</a>
        </p>
        <p><strong>Email:</strong> <a href="mailto:${this.returnValue(marker.email)}">${this.returnValue(marker.email)}</a></p>
        <p><strong>Phone:</strong> <a href="tel:${this.returnValue(marker.phone)}">${this.returnValue(marker.phone)}</a></p>
        </div>
      `
      if (this.currentMidx === index) {
        this.infoWinOpen = !this.infoWinOpen
      }
      else {
        this.infoWinOpen = true
        this.currentMidx = index
      }
    }
  },
  computed: {
    filteredMarkers() {
      if (this.catFilter.length === 0) return this.processMarkerList(this.locations.list)
      return this.processMarkerList(this.locations.list).filter(item => item.category && this.catFilter.includes(`${item.category.tid}`))
    }
  }
}
</script>

<style>
  .receiving-locations-map {
    position: relative;
  }
  .receiving-locations-map .rl-map {
    width: 100%;
    height: 500px;
  }
  .receiving-locations-map .place-search {
    position: absolute;
    min-width: 25%;
    width: 330px;
    top: 10px;
    left: 10px;
    z-index: 1001;
  }
  .receiving-locations-map .product-category-filter {
    position: absolute;
    width: 200px;
    top: 62px;
    left: 10px;
    z-index: 1000;
    max-height: calc(100% - 82px)
  }
  .receiving-locations-map .location-content p {
    margin: 0;
    line-height: 1.4;
    font-size: 14px;
    display: flex;
    flex-direction: row;
  }
  /* .receiving-locations-map .location-content p.address-line {
    display: flex;
    flex-direction: row;
  } */
  .receiving-locations-map .location-content p strong {
    min-width: 60px;
  }
  .receiving-locations-map .location-content p a {
    flex: 1;
    margin-left: 3px;
  }
</style>